import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mantine/core';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useTranslation } from '../context/TranslationContext';
import { formatWeight } from '../utils/formatWeight';
import { formatPrice } from '../utils/formatPrice';
import { localizedField } from '../utils/localizedField';
import { weightToPieces, weightFromPieces } from '../utils/weightPieces';
import { usePrimaryTouched } from '../hooks/usePrimaryTouched';
import PriceBreakdown from '../components/PriceBreakdown';
import AvailableOffers from '../components/offers/AvailableOffers';
import CartFreeOffersNotice from '../components/offers/CartFreeOffersNotice';
import './BusinessOrdering.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const WEIGHT_UNITS = ['g', 'kg', 'ml', 'L'];

// A variant is ordered by weight when it carries a pack size (weight_value +
// weight_unit). Bulk-only rows never get weight or pcs inputs.
function isWeightBased(v) {
  return v.weight_value != null && WEIGHT_UNITS.includes(v.weight_unit);
}

// Weight input unit for a variant: kg for mass products, L for volume ones.
function weightInputUnit(v) {
  const unit = String(v.weight_unit).toLowerCase();
  return unit === 'g' || unit === 'kg' ? 'kg' : 'L';
}

// Human-readable label for the input unit suffix (Ltr instead of L). The raw
// 'L' stays the conversion unit so weightToPieces/weightFromPieces keep working.
function weightDisplayUnit(v) {
  return weightInputUnit(v) === 'L' ? 'Ltr' : weightInputUnit(v);
}

export default function BusinessOrdering() {
  const { t, language } = useTranslation();
  const { isBusiness, loading: authLoading } = useAuth();
  const {
    cartItems,
    addToCart,
    updateQuantity,
    removeFromCart,
    getCartCount,
    couponDiscounts,
    ruleDiscount,
    ruleDiscountDescriptor,
    freeItems,
    availableOffers,
    shippingCost,
    shippingFree,
    volumeDiscount,
    volumeDiscountPercentage,
    appliedBalance,
    itemsRoundOffDiff,
    vatExclusive,
    netSubtotal,
    vatOnDiscount,
    itemVat,
    itemsTotal,
  } = useCart();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [weights, setWeights] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // null or { key, fallback, params }
  // SKUs whose quantity currently exceeds available stock: { [sku]: stock }.
  const [stockWarn, setStockWarn] = useState({});

  const cartSyncTimeoutRef = useRef({});
  const errorRef = useRef(null);
  // SKUs the user has edited this session. cartItems changes after each cart
  // sync must never re-derive/overwrite a field the user is actively editing.
  const touchedSkuRef = useRef(new Set());
  // SKUs whose weight the user has explicitly typed in. Until then, pcs edits
  // keep the weight in sync (weight is the derived value); once the user sets
  // a weight, pcs overrides never touch it again.
  const { markTouched: markWeightTouched, isTouched: isWeightTouched } = usePrimaryTouched();

  // Redirect if not business user (wait for auth to finish loading first)
  useEffect(() => {
    if (authLoading) return;
    if (!isBusiness) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isBusiness, authLoading]);

  // Flatten parent products into individual visible variant rows
  const variantRows = products.flatMap(p => {
    const parentName = localizedField(p, 'name', language);
    const visibleVariants = (p.variants || []).filter(v => !v.is_hidden);
    return visibleVariants.map(v => ({
      ...v,
      parentName,
      parentProductCode: p.product_code,
      is_bulk_only: p.is_bulk_only,
    }));
  });

  // Auto-fill quantities (and weights for weight-based products) from cart items.
  // Only populates SKUs the user hasn't touched — cart-sync refreshes of
  // cartItems must not clobber an in-progress weight/pcs edit.
  useEffect(() => {
    if (variantRows.length > 0 && cartItems.length > 0) {
      const cartQuantities = {};
      const cartWeights = {};
      cartItems.forEach(item => {
        if (touchedSkuRef.current.has(item.sku)) return;
        cartQuantities[item.sku] = item.quantity;
        const row = variantRows.find(r => r.sku === item.sku);
        if (row && isWeightBased(row)) {
          cartWeights[item.sku] = piecesToInputWeight(item.quantity, row);
        }
      });
      setQuantities(prev => ({ ...prev, ...cartQuantities }));
      setWeights(prev => ({ ...prev, ...cartWeights }));
    }
  }, [variantRows, cartItems]);

  const fetchData = async () => {
    try {
      const productsRes = await fetch(`${API_BASE_URL}/products`, { credentials: 'include' }).then(r => r.json());
      setProducts(productsRes);
    } catch (err) {
      console.error(err);
      setError({ key: 'errorLoading', fallback: 'Failed to load.', params: {} });
    } finally {
      setLoading(false);
    }
  };

  // Stock is a hard limit for non-preorder (can_preorder=false) variants and a
  // soft warning for preorder variants — quantities beyond available stock are
  // accepted there (we fulfill from the next production batch).
  const validateQuantity = (variantRow, newQuantity) => {
    if (!variantRow) return true;
    if (Number(variantRow.can_preorder) === 0 && newQuantity > variantRow.stock_quantity) {
      const productName = variantRow.parentName || localizedField(variantRow, 'name', language);
      setError({
        key: 'stockLimitError',
        fallback: `Cannot add more than ${variantRow.stock_quantity} items for ${productName}. Available stock: ${variantRow.stock_quantity}`,
        params: { stock: variantRow.stock_quantity, product: productName }
      });
      return false;
    }
    if (newQuantity > variantRow.stock_quantity) {
      setStockWarn(prev => ({ ...prev, [variantRow.sku]: variantRow.stock_quantity }));
    } else if (variantRow) {
      setStockWarn(prev => {
        const next = { ...prev };
        delete next[variantRow.sku];
        return next;
      });
    }
    return true;
  };

  // Sync quantity to cart with debouncing
  const syncToCart = (sku, newQuantity, product) => {
    if (cartSyncTimeoutRef.current[sku]) {
      clearTimeout(cartSyncTimeoutRef.current[sku]);
    }

    cartSyncTimeoutRef.current[sku] = setTimeout(async () => {
      try {
        const cartItem = cartItems.find(item => item.sku === sku);

        if (newQuantity === 0) {
          if (cartItem) {
            await removeFromCart(cartItem.cartItemId);
          }
        } else if (cartItem) {
          await updateQuantity(cartItem.cartItemId, newQuantity);
        } else if (product) {
          await addToCart(product, newQuantity);
        }
      } catch (err) {
        console.error('Failed to update cart:', err);
      }
    }, 500);
  };

  // Convert a raw weight input value (kg/L) into whole pieces, rounding up so the
  // order always covers at least the requested weight.
  const valToPieces = (val, variantRow) => {
    const num = Number(val);
    if (val === '' || !Number.isFinite(num) || num <= 0) return 0;
    const pieces = weightToPieces(num, weightInputUnit(variantRow), variantRow.weight_value, variantRow.weight_unit, { roundMode: 'up' });
    return pieces === null ? 0 : pieces;
  };

  // Express a piece count as the equivalent weight input (kg/L) for display.
  const piecesToInputWeight = (pieces, variantRow) => {
    const weight = weightFromPieces(pieces, variantRow.weight_value, variantRow.weight_unit, weightInputUnit(variantRow));
    if (weight === null || weight === 0) return '';
    return String(Number(weight.toFixed(3)));
  };

  // Handle weight input changes for weight-based products
  const handleWeightChange = (sku, val) => {
    const variantRow = variantRows.find(p => p.sku === sku);
    if (!variantRow || !isWeightBased(variantRow)) return;

    const pieces = valToPieces(val, variantRow);
    if (!validateQuantity(variantRow, pieces)) {
      return; // Don't update
    }

    setError(null);
    touchedSkuRef.current.add(sku);
    markWeightTouched(sku);
    setWeights(prev => ({ ...prev, [sku]: val }));
    setQuantities(prev => ({ ...prev, [sku]: pieces }));
    syncToCart(sku, pieces, variantRow);
  };

  // Handle manual piece edits for weight-based products (pcs stays editable).
  // Like inventory/fulfillment, the weight is a derived value: it follows pcs
  // until the user explicitly types a weight (then it's frozen). On the first
  // pcs-only entry the weight is auto-filled so the row stays consistent.
  const handlePcsChange = (sku, val) => {
    const variantRow = variantRows.find(p => p.sku === sku);
    if (!variantRow || !isWeightBased(variantRow)) return;

    const newVal = parseInt(val, 10);
    const pieces = isNaN(newVal) || newVal < 0 ? 0 : newVal;

    if (!validateQuantity(variantRow, pieces)) {
      return; // Don't update
    }

    setError(null);
    touchedSkuRef.current.add(sku);
    setQuantities(prev => ({ ...prev, [sku]: pieces }));
    if (!isWeightTouched(sku)) {
      setWeights(prev => ({ ...prev, [sku]: piecesToInputWeight(pieces, variantRow) }));
    }
    syncToCart(sku, pieces, variantRow);
  };

  // Handle plain quantity input changes for products without a pack weight
  const handleQtyChange = (sku, val) => {
    const newVal = parseInt(val, 10);
    const newQuantity = isNaN(newVal) || newVal < 0 ? 0 : newVal;

    // Find the variant row
    const variantRow = variantRows.find(p => p.sku === sku);

    // Validate the quantity (soft stock warning)
    if (variantRow && !validateQuantity(variantRow, newQuantity)) {
      return; // Don't update quantity
    }

    // Clear any previous error
    setError(null);
    touchedSkuRef.current.add(sku);

    // Update local state
    setQuantities(prev => ({
      ...prev,
      [sku]: newQuantity
    }));

    // Sync to cart
    syncToCart(sku, newQuantity, variantRow);
  };

  // Auto-scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  if (loading) return <div id="loading-spinner" role="status" aria-live="polite" className="p-8 text-center">{t('loading', 'Loading…')}</div>;

  return (
    <div id="business-ordering-container" className="business-ordering-container">
      <h2 id="business-ordering-title">{t('quickOrder', 'Quick Order')}</h2>

      {error ? <div ref={errorRef} id="error-banner" className="error-banner" role="alert">{t(error.key, error.fallback, error.params)}</div> : null}

      <p id="quick-order-hint" className="quick-order-hint">{t('quickOrderWeightHint', 'Weight-based products can be ordered by entering a total weight (e.g. kg); the piece count is calculated automatically and rounded up. The piece count stays editable.')}</p>

      <table id="products-table" className="products-table">
        <caption className="sr-only">{t('quickOrderTableCaption', 'Quick-order variants: product, SKU, price, stock, weight, piece count and line total. Stock status is announced per row.')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('product', 'Product')}</th>
            <th scope="col">{t('sku', 'SKU')}</th>
            <th scope="col">{t('priceExclVAT', 'Price (excl. VAT)')}</th>
            <th scope="col">{t('stock', 'Stock')}</th>
            <th scope="col">{t('weight', 'Weight')}</th>
            <th scope="col">{t('pcs', 'Pcs')}</th>
            <th scope="col">{t('total', 'Total')}</th>
          </tr>
        </thead>
        <tbody>
          {variantRows.map(v => {
            const price = (v.netPrice != null) ? (Number(v.netPrice) || 0) : (Number(v.price) || 0);
            const qty = quantities[v.sku] || 0;
            const rowTotal = qty * price;
            const displayName = v.parentName;
            const weightLabel = v.weight_value != null ? formatWeight(v.weight_value, v.weight_unit) : '';
            const fullLabel = `${displayName}${weightLabel ? ' ' + weightLabel : ''} (SKU ${v.sku})`;
            const isPreorder = Number(v.can_preorder) !== 0;
            const stockLabel = isPreorder || v.stock_quantity > 0 ? `${v.stock_quantity} in stock` : t('outOfStock', 'Out of stock');
            const stockColor = isPreorder
              ? '#495057'
              : (v.stock_quantity > 10 ? '#38a169' : v.stock_quantity > 0 ? '#d69e2e' : '#e53e3b');
            const weightBased = isWeightBased(v);
            const weightUnitLabel = weightDisplayUnit(v);

            return (
            <tr id={`product-row-${v.sku}`} key={v.sku}>
              <th scope="row">
                {displayName}
                {weightLabel ? <span style={{ color: '#888', marginLeft: 6, fontSize: '0.85em' }}>{weightLabel}</span> : null}
              </th>
              <td id={`product-sku-${v.sku}`}>{v.sku}</td>
              <td id={`product-price-${v.sku}`}>{formatPrice(price)}</td>
              <td id={`product-stock-${v.sku}`}>
                <span
                  aria-label={stockLabel}
                  style={{
                    color: stockColor,
                    fontWeight: '600',
                    fontSize: '0.9em'
                  }}
                >
                  {isPreorder || v.stock_quantity > 0 ? v.stock_quantity : t('outOfStock', 'Out')}
                </span>
              </td>
              <td id={`product-weight-${v.sku}`}>
                {v.is_bulk_only ? (
                  <span className="contact-to-order" role="note">{t('contactSupport', 'Contact Support')}</span>
                ) : weightBased ? (
                  <span className="weight-input-wrap">
                    <input
                      id={`weight-input-${v.sku}`}
                      type="number"
                      min="0"
                      step="0.001"
                      value={weights[v.sku] ?? ''}
                      onChange={(e) => handleWeightChange(v.sku, e.target.value)}
                      aria-label={`${t('weight', 'Weight')} for ${fullLabel}, in ${weightUnitLabel} (${stockLabel})`}
                    />
                    <span className="weight-input-unit" aria-hidden="true">{weightUnitLabel}</span>
                  </span>
                ) : (
                  <span className="weight-input-wrap">-</span>
                )}
              </td>
              <td>
                {v.is_bulk_only ? (
                  <span className="contact-to-order" role="note">{t('contactSupport', 'Contact Support')}</span>
                ) : (
                  <input
                    id={`qty-input-${v.sku}`}
                    type="number"
                    min="0"
                    max={isPreorder ? undefined : v.stock_quantity}
                    value={qty || ''}
                    onChange={(e) => weightBased ? handlePcsChange(v.sku, e.target.value) : handleQtyChange(v.sku, e.target.value)}
                    aria-label={`${t('pcs', 'Pcs')} for ${fullLabel} (${stockLabel})`}
                  />
                )}
              </td>
              <td id={`product-total-${v.sku}`}>
                {v.is_bulk_only ? '-' : (qty > 0 ? formatPrice(rowTotal) : '-')}
              </td>
            </tr>
          )})}
        </tbody>
      </table>

      {/* Soft stock warning (rows ordered beyond available stock) */}
      {Object.keys(stockWarn).length > 0 && (
        <div id="stock-warning-banner" className="stock-warning-banner" role="status" aria-live="polite">
          <p className="stock-warning-title">
            {t('stockShortfallTitle', 'Guaranteed Fresh & Crafted for You')}
          </p>
          <p className="stock-warning-message">
            {t('stockShortfallNote', 'We make everything in small, exclusive batches to ensure the highest quality. Your order is secured for our next production run and will be shipped right after. Thank you for choosing fresh!')}
          </p>
          <ul className="stock-warning-list">
            {Object.keys(stockWarn).map(sku => {
              const row = variantRows.find(v => v.sku === sku);
              return (
                <li key={sku}>
                  {row ? `${row.parentName}${row.weight_value != null ? ' ' + formatWeight(row.weight_value, row.weight_unit) : ''}` : sku}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div id="order-summary-box" className="order-summary-box">
        {/* Current Cart Status */}
        <div style={{ padding: '10px', background: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd' }}>
          <h2 style={{ margin: '0 0 5px 0', fontSize: '0.9rem', fontWeight: '600' }}>{t('cartStatus', 'Your Cart')}:</h2>
          <p style={{ margin: '0', fontSize: '0.85rem' }}>{t('itemsInCart', 'Items in cart')}: {getCartCount()}</p>

          {getCartCount() > 0 && (
            <>
              <PriceBreakdown
                t={t}
                couponDiscounts={couponDiscounts}
                ruleDiscount={ruleDiscount}
                ruleDiscountLabel={ruleDiscountDescriptor || undefined}
                hideShipping
                shippingCost={shippingCost}
                shippingFree={shippingFree}
                volumeDiscount={volumeDiscount}
                volumeDiscountPercentage={volumeDiscountPercentage}
                appliedBalance={appliedBalance}
                roundOffDiff={itemsRoundOffDiff}
                vatExclusive={vatExclusive}
                netSubtotal={netSubtotal}
                vatOnDiscount={vatOnDiscount}
                vatAmount={itemVat}
              />
              <p style={{ margin: '5px 0 0', fontSize: '0.9rem', fontWeight: '700' }}>
                {t('totalPayable', 'Total payable')}: {formatPrice(itemsTotal)}
              </p>
            </>
          )}
        </div>

        {/* Achievable offers + free items */}
        <CartFreeOffersNotice freeItems={freeItems} />
        <AvailableOffers offers={availableOffers} />

        {/* View Cart / Checkout Button */}
        {getCartCount() > 0 && (
          <Button
            id="view-cart-button"
            variant="filled"
            color="green"
            onClick={() => navigate('/cart')}
            mt="md"
          >
            {t('viewCart', 'View Cart')} & {t('checkout', 'Checkout')}
          </Button>
        )}
      </div>
    </div>
  );
}
